import { QueryClient, notifyManager } from "@tanstack/react-query";
import { ModelName, TellescopeQueryUpdateWithTimestamp } from "../types";
import { parseTellescopeQueryKey, setTellescopeQueryData } from "../utils";

/**
 * Arguments for rolling back cache updates after a failed mutation.
 */
export interface RollbackCacheUpdatesArgs<Model extends ModelName = ModelName> {
  /** The TanStack Query client instance to rollback updates on */
  queryClient: QueryClient;
  /** Array of queries with their original data and timestamps to restore */
  originalQueries: TellescopeQueryUpdateWithTimestamp<Model>[];
}

/**
 * Rolls back cache updates to their original state.
 * This function is typically called when a mutation fails, to revert optimistic updates
 * back to their previous values with their original timestamps.
 *
 * @param args - Configuration object containing queryClient and original queries to restore
 * @returns An array of the restored query data
 *
 * @example
 * ```ts
 * // After a failed mutation, rollback optimistic updates
 * rollbackCacheUpdates({
 *   queryClient,
 *   originalQueries: context.originalData,
 * });
 * ```
 */
export const rollbackCacheUpdates = <Model extends ModelName = ModelName>({
  queryClient,
  originalQueries,
}: RollbackCacheUpdatesArgs<Model>) => {
  // Wrapped in notifyManager.batch() so observer notifications are coalesced
  // into a single React render pass when rolling back multiple queries at once.
  return notifyManager.batch(() =>
    originalQueries.map(([queryKey, data, updatedAt]) => {
      if (data === undefined) {
        // If original data was undefined, remove the query from cache
        queryClient.removeQueries({ queryKey, exact: true });
        return undefined;
      }
      const { model, method, request } = parseTellescopeQueryKey(queryKey);
      if (!request) return undefined;
      const [, originalQueryData] = setTellescopeQueryData<Model>({
        queryClient,
        updater: data,
        model,
        method,
        request,
        options: { updatedAt },
      }).updatedQuery;
      return originalQueryData;
    })
  );
};
